import { X } from 'lucide-react';
import * as React from 'react';
import { EXPERTISE_AREA_TAXONOMY } from '../../../helpers/expertiseAreaTaxonomy';
import { humanize } from '../../../helpers/interestsTaxonomy';

const MAX_VISIBLE_OPTIONS = 8;

interface Props {
  value: string[];
  onChange: (next: string[]) => void;
  onSuggestNew: (query: string) => void;
}

export function ExpertiseAutocomplete({
  value,
  onChange,
  onSuggestNew,
}: Props): React.ReactElement {
  const [query, setQuery] = React.useState('');
  const [isOpen, setIsOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleClick = (e: MouseEvent): void => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const selectedSet = React.useMemo(() => new Set(value), [value]);

  const filteredOptions = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return EXPERTISE_AREA_TAXONOMY.filter((tax) => !selectedSet.has(tax))
      .filter((tax) => humanize(tax).toLowerCase().includes(q))
      .slice(0, MAX_VISIBLE_OPTIONS);
  }, [query, selectedSet]);

  const hasNoMatches = query.trim().length > 0 && filteredOptions.length === 0;

  const handleAdd = (tax: string): void => {
    onChange([...value, tax]);
    setQuery('');
    setIsOpen(false);
  };

  const handleRemove = (tax: string): void => {
    onChange(value.filter((v) => v !== tax));
  };

  return (
    <div className="plan-c-form__expertise-autocomplete" ref={containerRef}>
      <input
        type="text"
        placeholder="Search areas of expertise…"
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setIsOpen(true);
        }}
        onFocus={() => setIsOpen(true)}
        className="plan-c-form__input"
        aria-label="Search areas of expertise"
        role="combobox"
        aria-expanded={isOpen}
        aria-controls="expertise-autocomplete-listbox"
      />
      {isOpen && filteredOptions.length > 0 && (
        <ul
          id="expertise-autocomplete-listbox"
          className="plan-c-form__expertise-options"
          role="listbox"
        >
          {filteredOptions.map((tax) => (
            <li
              key={tax}
              role="option"
              aria-selected={false}
              className="plan-c-form__expertise-option"
              onMouseDown={(e) => {
                e.preventDefault();
                handleAdd(tax);
              }}
            >
              {humanize(tax)}
            </li>
          ))}
        </ul>
      )}
      {isOpen && hasNoMatches && (
        <div className="plan-c-form__expertise-no-match">
          <p>No matches for "{query}".</p>
          <button
            type="button"
            className="plan-c-form__suggest-link"
            onClick={() => onSuggestNew(query)}
          >
            Don't see your field? Suggest one →
          </button>
        </div>
      )}
      {value.length > 0 && (
        <div className="plan-c-form__expertise-chips">
          {value.map((tax) => (
            <span key={tax} className="chip chip--selected">
              {humanize(tax)}
              <button
                type="button"
                aria-label={`Remove ${humanize(tax)}`}
                onClick={() => handleRemove(tax)}
                className="chip__remove"
              >
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
